import { API_BASE_URL, ENDPOINTS } from '../config/api';

// ── Token storage (Bearer auth - works cross-origin / Cloudflare / VPS) ──────
const ACCESS_KEY  = 'nc_access_token';
const REFRESH_KEY = 'nc_refresh_token';

export const getAccess   = () => { try { return localStorage.getItem(ACCESS_KEY); } catch { return null; } };
export const getRefresh  = () => { try { return localStorage.getItem(REFRESH_KEY); } catch { return null; } };
export const setAccess   = (t) => { try { t ? localStorage.setItem(ACCESS_KEY, t)  : localStorage.removeItem(ACCESS_KEY);  } catch {} };
export const setRefresh  = (t) => { try { t ? localStorage.setItem(REFRESH_KEY, t) : localStorage.removeItem(REFRESH_KEY); } catch {} };
export const clearTokens = () => { setAccess(null); setRefresh(null); };

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

export async function fetchWithAuth(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = getAccess();
  const config = {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  };

  if (options.body && typeof options.body !== 'string') {
    config.body = JSON.stringify(options.body);
  }

  let response = await fetch(url, config);

  if (response.status === 401 && !options._retry) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => fetchWithAuth(endpoint, { ...options, _retry: true }));
    }

    isRefreshing = true;
    try {
      const rt = getRefresh();
      const refreshResp = rt
        ? await fetch(`${API_BASE_URL}${ENDPOINTS.AUTH.REFRESH}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: rt }),
          })
        : null;

      if (!refreshResp || !refreshResp.ok) {
        clearTokens();
        processQueue(new Error('Session expired'));
        isRefreshing = false;
        window.location.href = '/login';
        return;
      }

      const data = await refreshResp.json().catch(() => ({}));
      if (data.access_token)  setAccess(data.access_token);
      if (data.refresh_token) setRefresh(data.refresh_token);

      processQueue(null);
      isRefreshing = false;
      return fetchWithAuth(endpoint, { ...options, _retry: true });
    } catch (err) {
      clearTokens();
      processQueue(err);
      isRefreshing = false;
      window.location.href = '/login';
      return;
    }
  }

  return response;
}

export async function apiGet(endpoint, params = {}) {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  const url = query ? `${endpoint}?${query}` : endpoint;
  const resp = await fetchWithAuth(url, { method: 'GET' });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    const detail = err.detail;
    const msg = Array.isArray(detail)
      ? detail.map(e => `${e.loc?.slice(-1)[0] ?? ''}: ${e.msg}`).join(' | ')
      : (detail || err.message || `Request failed: ${resp.status}`);
    throw new Error(msg);
  }
  return resp.json();
}

export async function apiPost(endpoint, body = {}) {
  const resp = await fetchWithAuth(endpoint, { method: 'POST', body });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    const detail = err.detail;
    const msg = Array.isArray(detail)
      ? detail.map(e => `${e.loc?.slice(-1)[0] ?? ''}: ${e.msg}`).join(' | ')
      : (detail || err.message || `Request failed: ${resp.status}`);
    throw new Error(msg);
  }
  return resp.json();
}

export async function apiPut(endpoint, body = {}) {
  const resp = await fetchWithAuth(endpoint, { method: 'PUT', body });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    const detail = err.detail;
    const msg = Array.isArray(detail)
      ? detail.map(e => `${e.loc?.slice(-1)[0] ?? ''}: ${e.msg}`).join(' | ')
      : (detail || err.message || `Request failed: ${resp.status}`);
    throw new Error(msg);
  }
  return resp.json();
}

export async function apiPatch(endpoint, body = {}) {
  const resp = await fetchWithAuth(endpoint, { method: 'PATCH', body });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    const detail = err.detail;
    const msg = Array.isArray(detail)
      ? detail.map(e => `${e.loc?.slice(-1)[0] ?? ''}: ${e.msg}`).join(' | ')
      : (detail || err.message || `Request failed: ${resp.status}`);
    throw new Error(msg);
  }
  return resp.json();
}

export async function apiDelete(endpoint) {
  const resp = await fetchWithAuth(endpoint, { method: 'DELETE' });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    const detail = err.detail;
    const msg = Array.isArray(detail)
      ? detail.map(e => `${e.loc?.slice(-1)[0] ?? ''}: ${e.msg}`).join(' | ')
      : (detail || err.message || `Request failed: ${resp.status}`);
    throw new Error(msg);
  }
  return resp.status === 204 ? null : resp.json();
}
